import React from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { ArrowDownTrayIcon, HomeIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { products } from '../data/products';
import { courses } from '../data/courses';
import { getProductDownloadLink } from '../utils/productLinks';
import { getCourseDownloadLink } from '../utils/courseLinks';
import { selectPurchasedProducts, selectPurchasedCourses } from '../store/store';

const MyPurchases = () => {
  const purchasedProductIds = useSelector(selectPurchasedProducts);
  const purchasedCourseIds = useSelector(selectPurchasedCourses);
  const purchasedProducts = products.filter(product => 
    purchasedProductIds.includes(product.id)
  );
  const purchasedCourses = courses.filter(course => 
    purchasedCourseIds.includes(course.id)
  );

  const handleDownload = (itemId, type) => {
    const downloadLink = type === 'course' ? getCourseDownloadLink(itemId) : getProductDownloadLink(itemId);
    if (downloadLink) {
      window.open(downloadLink, '_blank');
    } else {
      toast.error('Download link not available. Please contact support.');
    }
  };

  const allPurchasedItems = [
    ...purchasedProducts.map(product => ({ ...product, type: 'product' })),
    ...purchasedCourses.map(course => ({ ...course, type: 'course' }))
  ];

  return (
    <>
      <Helmet>
        <title>Pingfence | My Purchases</title>
        <meta name="description" content="Access all your purchased digital products and downloads." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      
      <div className="container py-12">
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center space-x-2 text-sm text-gray-800 mb-8">
          <Link to="/" className="hover:text-primary transition-colors">
            <HomeIcon className="h-5 w-5" />
          </Link>
          <ChevronRightIcon className="h-4 w-4" />
          <span className="text-primary">My Purchases</span>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-4">My Purchases</h1>
          <p className="text-gray-800">
            Access all your purchased digital products and downloads
          </p>
        </div>

        {/* Purchased Items */}
        {allPurchasedItems.length === 0 ? (
          <div className="text-center py-16">
            <div className="max-w-md mx-auto">
              <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <ArrowDownTrayIcon className="h-12 w-12 text-primary" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">No purchases yet</h3>
              <p className="text-gray-800 mb-8">
                You haven't purchased any products yet. Explore our collection and find something amazing!
              </p>
              <Link
                to="/products"
                className="inline-flex items-center px-6 py-3 bg-primary hover:bg-primary-dark text-black font-semibold rounded-xl transition-all duration-300 hover:scale-105 shadow-[0_0_20px_rgba(159,226,64,0.3)] hover:shadow-[0_0_30px_rgba(159,226,64,0.5)]"
              >
                Browse Products
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allPurchasedItems.map((item) => (
              <div
                key={`${item.type}-${item.id}`}
                className="bg-secondary border-2 border-primary/20 rounded-2xl overflow-hidden hover:shadow-[0_0_30px_rgba(74,222,128,0.4)] hover:border-primary/40 transition-all duration-500"
              >
                {/* Item Image */}
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="object-cover w-full h-full"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-secondary via-secondary/20 to-transparent opacity-60" />
                  
                  {/* Purchased Badge */}
                  <div className="absolute top-4 right-4">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-600 text-black rounded-full shadow-[0_0_20px_rgba(34,197,94,0.4)]">
                      <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                      Purchased
                    </span>
                  </div>
                  
                  {/* Type Badge */}
                  <div className="absolute top-4 left-4">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full shadow-lg ${
                      item.type === 'course' 
                        ? 'bg-blue-600 text-black' 
                        : 'bg-purple-600 text-black'
                    }`}>
                      {item.type === 'course' ? 'Course' : 'Product'}
                    </span>
                  </div>
                </div>

                {/* Item Info */}
                <div className="p-6 space-y-4">
                  <div>
                    <h3 className="text-xl font-semibold text-black mb-2">
                      {item.name}
                    </h3>
                    <p className="text-gray-800 text-sm line-clamp-2">
                      {item.shortDescription || item.description}
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-primary">
                      ₹{item.price.toLocaleString('en-IN')}
                    </span>
                    
                    <button
                      onClick={() => handleDownload(item.id, item.type)}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-black rounded-xl transition-all duration-300 hover:scale-105 shadow-[0_0_15px_rgba(34,197,94,0.4)]"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      <span className="text-sm font-medium">Download</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary */}
        {allPurchasedItems.length > 0 && (
          <div className="mt-12 p-6 bg-primary/10 rounded-2xl border border-primary/20">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-black mb-2">
                  Purchase Summary
                </h3>
                <p className="text-gray-800">
                  You have purchased {allPurchasedItems.length} item{allPurchasedItems.length !== 1 ? 's' : ''} 
                  ({purchasedProducts.length} product{purchasedProducts.length !== 1 ? 's' : ''}, {purchasedCourses.length} course{purchasedCourses.length !== 1 ? 's' : ''})
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-primary">
                  ₹{allPurchasedItems.reduce((total, item) => total + item.price, 0).toLocaleString('en-IN')}
                </p>
                <p className="text-sm text-gray-800">Total Spent</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MyPurchases;
